import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { ASSETS, HERO_STATS } from "../data";
import { scrollToId } from "./Navbar";

const line = {
  hidden: { y: "120%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 1 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const hazeY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const iconY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex flex-col justify-end"
    >
      {/* Blue haze */}
      <motion.div
        style={{ y: hazeY }}
        className="absolute inset-0 haze-radial"
        aria-hidden
      />
      {/* Photo backdrop */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-40"
        style={{ backgroundImage: `url(${ASSETS.djBoothNight})` }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" aria-hidden />

      {/* Floating lantern icon */}
      <motion.img
        src={ASSETS.logoIcon}
        alt=""
        aria-hidden
        style={{ y: iconY }}
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 0.9, scale: 1 }}
        transition={{ duration: 1.6, delay: 0.4, ease: "easeOut" }}
        className="absolute right-[4%] top-[16%] w-40 md:w-72 object-contain pointer-events-none drop-shadow-[0_0_60px_rgba(0,51,153,0.6)]"
      />

      <motion.div
        style={{ y: titleY, opacity: fade }}
        className="relative max-w-7xl mx-auto w-full px-6 md:px-12 pb-16 md:pb-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="text-[#003399] font-bold text-xs md:text-sm uppercase tracking-[0.35em] mb-6"
          style={{ textShadow: "0 0 20px rgba(0,51,153,0.9)" }}
        >
          DJ · Producer · Recording Engineer
        </motion.p>

        <h1 className="font-display text-[#F2F2F2] leading-[0.92] tracking-tight text-6xl sm:text-7xl md:text-8xl lg:text-[9.5rem]">
          <span className="block overflow-hidden">
            <motion.span variants={line} custom={0} initial="hidden" animate="show" className="block">
              TAO
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              variants={line}
              custom={1}
              initial="hidden"
              animate="show"
              className="block text-outline-blue"
            >
              LUMIN
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-book-btn"
            onClick={() => scrollToId("contact")}
            className="px-8 py-4 bg-[#003399] text-[#F2F2F2] text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_32px_rgba(0,51,153,0.85)] transition-shadow duration-300"
          >
            Book TAO LUMIN
          </button>
          <a
            data-testid="hero-presskit-btn"
            href="https://customer-assets-wrfwihn1.emergentagent.net/job_593c7add-b54b-46f4-8378-e7c0f128ee44/artifacts/6bpyol6s_Tao_Lumin%20-%20Press%20Kit.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 border border-white/25 text-[#F2F2F2] text-xs uppercase tracking-[0.2em] hover:border-[#003399] hover:text-[#003399] transition-colors duration-300"
          >
            Press Kit <Download size={15} />
          </a>
        </motion.div>

        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 1 }}
            className="max-w-md text-[#F2F2F2]/70 text-sm md:text-base font-light leading-relaxed"
          >
            High-energy peak-time house, deep funk grooves and chilled lounge
            sets — TAO LUMIN plays the full spectrum of house and takes every
            crowd on a journey, from Nassau to international stages.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.9, duration: 1 }}
            className="flex gap-10"
          >
            {HERO_STATS.map((s) => (
              <div key={s.k} data-testid={`hero-stat-${s.k}`}>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#F2F2F2]/40">
                  {s.k}
                </div>
                <div className="font-display text-lg md:text-xl text-[#F2F2F2] mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("about")}
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2, duration: 1 }}
        className="absolute left-1/2 -translate-x-1/2 bottom-6 flex flex-col items-center gap-2 text-[#F2F2F2]/50 hover:text-[#F2F2F2] transition-colors"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
        >
          <ArrowDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
